import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit3, Trash2, Eye, EyeOff, X, Save, Upload, Search, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PackModal = ({ pack, tracks, onClose, onSave, hideFinancials }) => {
  const [form, setForm] = useState(pack || {
    name: '', description: '', genre: '', mood: '', track_count: 5,
    price: 29.99, cover_image: '', formats: ['WAV', 'MP3'], track_ids: [], artist: '', is_visible: true
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [trackSearch, setTrackSearch] = useState('');

  const selectedTrackIds = form.track_ids || [];
  const availableTracks = tracks.filter(t => !selectedTrackIds.includes(t.id) && (
    !trackSearch || t.title?.toLowerCase().includes(trackSearch.toLowerCase())
  ));

  const addTrack = (trackId) => setForm({ ...form, track_ids: [...selectedTrackIds, trackId], track_count: selectedTrackIds.length + 1 });
  const removeTrack = (trackId) => setForm({ ...form, track_ids: selectedTrackIds.filter(id => id !== trackId), track_count: Math.max(0, selectedTrackIds.length - 1) });

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/api/admin/upload/images`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm({ ...form, cover_image: r.data.url });
    } catch { alert('Error subiendo imagen'); }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...form, track_count: parseInt(form.track_count), price: parseFloat(form.price),
        formats: Array.isArray(form.formats) ? form.formats : form.formats.split(',').map(f => f.trim())
      });
    } catch (err) { alert(err.response?.data?.detail || 'Error guardando pack'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="glass-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pack-form-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">{pack ? 'Editar Pack' : 'Agregar Pack'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {[{ key: 'name', label: 'Nombre', required: true }, { key: 'description', label: 'Descripcion', required: true }, { key: 'genre', label: 'Genero', required: true }, { key: 'mood', label: 'Mood', required: true }, { key: 'artist', label: 'Artista (opcional)' }].map(f => (
            <div key={f.key}><label className="block text-xs text-white/50 mb-1">{f.label}</label>
              <input className="w-full input-glass" value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} required={f.required} data-testid={`pack-input-${f.key}`} /></div>
          ))}
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-white/50 mb-1">Tracks en Pack</label><input type="number" className="w-full input-glass" value={form.track_count} onChange={e => setForm({ ...form, track_count: e.target.value })} data-testid="pack-input-track_count" /></div>
            {!hideFinancials && <div><label className="block text-xs text-white/50 mb-1">Precio ($)</label><input type="number" step="0.01" className="w-full input-glass" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} data-testid="pack-input-price" /></div>}
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-2">Tracks Asignados ({selectedTrackIds.length})</label>
            {selectedTrackIds.length > 0 && (
              <div className="space-y-1 mb-3">
                {selectedTrackIds.map(tid => {
                  const tr = tracks.find(t => t.id === tid);
                  return tr ? (
                    <div key={tid} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                      <span className="text-sm">{tr.title} <span className="text-white/40">- {tr.artist}</span></span>
                      <button type="button" onClick={() => removeTrack(tid)} className="text-red-400 hover:text-red-300 p-1"><X className="w-4 h-4" /></button>
                    </div>
                  ) : null;
                })}
              </div>
            )}
            <input className="w-full input-glass text-sm mb-2" placeholder="Buscar tracks para agregar..." value={trackSearch} onChange={e => setTrackSearch(e.target.value)} data-testid="pack-track-search" />
            {trackSearch && availableTracks.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-1 border border-white/10 rounded-lg p-2">
                {availableTracks.slice(0, 10).map(t => (
                  <button type="button" key={t.id} onClick={() => { addTrack(t.id); setTrackSearch(''); }}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/10 rounded-lg text-sm text-left">
                    <span>{t.title} <span className="text-white/40">- {t.artist}</span></span>
                    <Plus className="w-4 h-4 text-[#E8720C]" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="button" onClick={() => setForm({ ...form, is_visible: !form.is_visible })}
            className={`w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${form.is_visible ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
            {form.is_visible ? <><Eye className="w-4 h-4" />Visible en Tienda</> : <><EyeOff className="w-4 h-4" />Oculto</>}
          </button>
          <button type="submit" disabled={saving} className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="pack-form-save">
            <Save className="w-4 h-4" />{saving ? 'Guardando...' : 'Guardar Pack'}
          </button>
        </form>
      </div>
    </div>
  );
};

const PacksPanelFull = ({ hideFinancials = false }) => {
  const [packs, setPacks] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    try {
      const [p, t] = await Promise.all([
        axios.get(`${API}/api/admin/packs`, { withCredentials: true }),
        axios.get(`${API}/api/admin/tracks`, { withCredentials: true }),
      ]);
      setPacks(p.data);
      setTracks(t.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (formData) => {
    try {
      if (formData.id) {
        await axios.put(`${API}/api/admin/packs/${formData.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/packs`, formData, { withCredentials: true });
      }
      setModal(null);
      load();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este pack?')) return;
    try {
      await axios.delete(`${API}/api/admin/packs/${id}`, { withCredentials: true });
      load();
    } catch { alert('Error eliminando'); }
  };

  const handleToggleVisibility = async (id, current) => {
    try {
      await axios.put(`${API}/api/admin/packs/${id}/visibility`, { is_visible: !current }, { withCredentials: true });
      load();
    } catch { alert('Error'); }
  };

  if (loading) return <p className="text-white/40">Cargando packs...</p>;

  return (
    <div data-testid="packs-panel-full">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="text-2xl font-bold">Packs ({packs.length})</h2>
        <button onClick={() => setModal({ data: null })} className="btn-primary py-2 px-5 flex items-center gap-2 text-sm" data-testid="add-pack-btn">
          <Plus className="w-4 h-4" /> Agregar Pack
        </button>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-white/40 border-b border-white/10">
              <th className="p-4">Nombre</th><th className="p-4">Genero</th><th className="p-4">Tracks</th>
              {!hideFinancials && <th className="p-4">Precio</th>}
              <th className="p-4 text-center">Visible</th><th className="p-4 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {packs.map(pack => (
                <tr key={pack.id} className={`border-b border-white/5 hover:bg-white/5 ${!pack.is_visible ? 'opacity-40' : ''}`} data-testid={`admin-pack-row-${pack.id}`}>
                  <td className="p-4 font-medium">{pack.name} {pack.is_ultimate && <span className="text-xs text-[#E8720C]">(Ultimate)</span>}</td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full text-xs bg-white/10">{pack.genre}</span></td>
                  <td className="p-4 text-white/60">{pack.track_count}</td>
                  {!hideFinancials && <td className="p-4 gold-text font-medium">${pack.price}</td>}
                  <td className="p-4 text-center">
                    <button onClick={() => handleToggleVisibility(pack.id, pack.is_visible)} className={`p-1.5 rounded-lg transition-colors ${pack.is_visible !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`} data-testid={`pack-visibility-${pack.id}`}>
                      {pack.is_visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setModal({ data: pack })} className="p-2 hover:bg-white/10 rounded-lg" data-testid={`edit-pack-${pack.id}`}><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(pack.id)} className="p-2 hover:bg-red-500/20 rounded-lg text-red-400" data-testid={`delete-pack-${pack.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {packs.length === 0 && <p className="text-center text-white/40 py-8">Sin packs</p>}
      </div>
      {modal && <PackModal pack={modal.data} tracks={tracks} onClose={() => setModal(null)} onSave={handleSave} hideFinancials={hideFinancials} />}
    </div>
  );
};

export default PacksPanelFull;
